import React, { useState } from 'react';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  ComposedChart,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import { BarChart3, TrendingUp, Calendar, Activity, Plus, Minus, Move, Eye, EyeOff, Shield, AlertTriangle } from 'lucide-react';
import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const StockChart = ({ data, loading, error, symbol, onDateRangeChange }) => {
  const { t } = useLanguage();
  const [chartType, setChartType] = useState('line');
  const [dateRange, setDateRange] = useState('1y'); // 'all', '1y', '2y'
  const [showSupportResistance, setShowSupportResistance] = useState(true);
  
  // Initialize zoom based on screen size - mobile gets zoomed view, desktop shows all
  const [zoomLevel, setZoomLevel] = useState(() => {
    const isMobile = window.innerWidth <= 768;
    return isMobile ? 15 : 100; // Mobile: show ~15% (zoomed in), Desktop: show all
  });
  const [yZoomLevel, setYZoomLevel] = useState(100); // Y-axis zoom level
  const [panOffset, setPanOffset] = useState(0); // Horizontal pan offset
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState('pan'); // 'pan' or 'zoom'
  const [lastDragX, setLastDragX] = useState(0);
  const [lastDragY, setLastDragY] = useState(0);
  const [showZoomControls, setShowZoomControls] = useState(() => {
    const saved = localStorage.getItem('showZoomControls');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [isPinching, setIsPinching] = useState(false);

  // Handle window resize to adjust zoom level for mobile/desktop
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768;
      // Only adjust if we're at 100% zoom and switching to mobile, or if we're zoomed and switching to desktop
      if (zoomLevel >= 100 && isMobile) {
        setZoomLevel(15); // Switch to mobile zoom
      } else if (zoomLevel < 100 && !isMobile && zoomLevel < 50) {
        setZoomLevel(100); // Switch to desktop view if heavily zoomed on mobile
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [zoomLevel]);

  // Get filtered data based on zoom level and pan offset
  const getFilteredData = () => {
    if (!data || data.length === 0) return [];
    
    const totalPoints = data.length;
    
    // At 100% zoom level, show ALL available data without any filtering
    if (zoomLevel >= 100) {
      return data;
    }
    
    // Calculate how many points to show based on zoom level
    const pointsToShow = Math.max(10, Math.floor((totalPoints * zoomLevel) / 100));
    
    // If calculated points >= total points, return all data
    if (pointsToShow >= totalPoints) {
      return data;
    }
    
    // Apply pan offset to determine which slice of data to show
    // Start from the most recent data and work backwards
    const maxOffset = Math.max(0, totalPoints - pointsToShow);
    const clampedOffset = Math.max(0, Math.min(maxOffset, panOffset));
    const startIndex = totalPoints - pointsToShow - clampedOffset;
    const endIndex = startIndex + pointsToShow;
    
    return data.slice(Math.max(0, startIndex), Math.min(totalPoints, endIndex));
  };

  // Calculate Y-axis domain based on zoom level
  const getYAxisDomain = () => {
    const filteredData = getFilteredData();
    if (!filteredData || filteredData.length === 0) return ['dataMin - 5', 'dataMax + 5'];
    
    // Get min and max values from filtered data
    const values = filteredData.flatMap(d => [d.low, d.high, d.open, d.close]).filter(v => v != null);
    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);
    const range = dataMax - dataMin;
    
    if (yZoomLevel === 100) {
      // Default zoom - show all with padding
      return [dataMin - range * 0.05, dataMax + range * 0.05];
    }
    
    // Apply Y-axis zoom
    const center = (dataMin + dataMax) / 2;
    const zoomedRange = range * (yZoomLevel / 100);
    const yMin = Math.max(dataMin - range * 0.1, center - zoomedRange / 2);
    const yMax = Math.min(dataMax + range * 0.1, center + zoomedRange / 2);
    
    return [yMin, yMax];
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.max(10, prev - 20)); // Minimum 10% (show at least some data)
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.min(100, prev + 20)); // Maximum 100% (show all data)
  };

  const handleYZoomIn = () => {
    setYZoomLevel(prev => Math.max(10, prev - 20));
  };

  const handleYZoomOut = () => {
    setYZoomLevel(prev => Math.min(200, prev + 20)); // Allow 200% Y zoom for detailed view
  };

  const handleResetZoom = () => {
    setZoomLevel(100);
    setYZoomLevel(100);
    setPanOffset(0);
  };

  const toggleZoomControls = () => {
    const newValue = !showZoomControls;
    setShowZoomControls(newValue);
    localStorage.setItem('showZoomControls', JSON.stringify(newValue));
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    // Reset zoom and pan when changing date range
    // On mobile, start zoomed in; on desktop, show all
    const isMobile = window.innerWidth <= 768;
    setZoomLevel(isMobile ? 15 : 100);
    setYZoomLevel(100);
    setPanOffset(0);
    
    // Call parent component to fetch new data
    if (onDateRangeChange) {
      onDateRangeChange(newRange);
    }
  };

  // Toggle between showing all data and zoomed view
  const handleToggleZoomView = () => {
    const isMobile = window.innerWidth <= 768;
    if (zoomLevel >= 100) {
      // Currently showing all, switch to zoomed view
      setZoomLevel(isMobile ? 15 : 50);
      setPanOffset(0); // Start at most recent data
    } else {
      // Currently zoomed, switch to show all
      setZoomLevel(100);
      setPanOffset(0);
    }
  };

  // Mouse drag handlers for pan and zoom
  const handleMouseDown = (e) => {
    // Determine drag mode based on modifier keys
    if (e.shiftKey) {
      setDragMode('zoom');
    } else {
      setDragMode('pan');
    }
    
    setIsDragging(true);
    setLastDragX(e.clientX);
    setLastDragY(e.clientY);
    document.body.style.cursor = dragMode === 'zoom' ? 'zoom-in' : 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastDragX;
    const deltaY = e.clientY - lastDragY;
    
    if (dragMode === 'zoom') {
      // Vertical zoom (up/down mouse movement)
      const yZoomSensitivity = 0.3;
      const yZoomChange = -deltaY * yZoomSensitivity; // Negative to make up = zoom in
      setYZoomLevel(prev => Math.max(10, Math.min(200, prev + yZoomChange)));
      
      // Horizontal zoom (left/right mouse movement) 
      const xZoomSensitivity = 0.3;
      const xZoomChange = deltaX * xZoomSensitivity;
      setZoomLevel(prev => Math.max(10, Math.min(100, prev + xZoomChange)));
    } else {
      // Pan mode - left/right movement pans through data
      const panSensitivity = 0.5;
      const panChange = -deltaX * panSensitivity; // Negative to make right = go forward in time
      const totalPoints = data?.length || 0;
      const pointsToShow = Math.max(10, Math.floor((totalPoints * zoomLevel) / 100));
      const maxPanOffset = Math.max(0, totalPoints - pointsToShow);
      
      setPanOffset(prev => Math.max(0, Math.min(maxPanOffset, prev + panChange)));
    }
    
    setLastDragX(e.clientX);
    setLastDragY(e.clientY);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = 'default';
  };

  // Helper function to calculate distance between two touches
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Touch drag handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      setDragMode('pan'); // Default to pan for touch
      setIsDragging(true);
      setIsPinching(false);
      setLastDragX(touch.clientX);
      setLastDragY(touch.clientY);
    } else if (e.touches.length === 2) {
      // Pinch gesture
      setIsPinching(true);
      setIsDragging(false);
      const distance = getTouchDistance(e.touches);
      setInitialPinchDistance(distance);
    }
  };

  const handleTouchMove = (e) => {
    // Only prevent default if we're actually handling the gesture
    if ((isPinching && e.touches.length === 2) || (isDragging && e.touches.length === 1)) {
      // Try to prevent default, but don't break if it's a passive listener
      try {
        e.preventDefault();
      } catch (error) {
        // Passive event listener - preventDefault not allowed, continue without it
      }
    }
    
    if (isPinching && e.touches.length === 2) {
      // Handle pinch-to-zoom
      const distance = getTouchDistance(e.touches);
      const scale = distance / initialPinchDistance;
      
      // Apply zoom based on pinch scale (X-axis zoom)
      const zoomChange = (scale - 1) * 50; // Sensitivity adjustment
      setZoomLevel(prev => Math.max(10, Math.min(100, prev + zoomChange)));
      setInitialPinchDistance(distance);
    } else if (isDragging && e.touches.length === 1) {
      // Handle single-finger gestures
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastDragX;
      const deltaY = touch.clientY - lastDragY;
      
      // For touch, prioritize the larger movement direction
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        // Vertical movement - Y zoom
        const yZoomSensitivity = 0.2;
        const yZoomChange = -deltaY * yZoomSensitivity;
        setYZoomLevel(prev => Math.max(10, Math.min(200, prev + yZoomChange)));
      } else {
        // Horizontal movement - pan
        const panSensitivity = 0.3;
        const panChange = -deltaX * panSensitivity;
        const totalPoints = data?.length || 0;
        const pointsToShow = Math.max(10, Math.floor((totalPoints * zoomLevel) / 100));
        const maxPanOffset = Math.max(0, totalPoints - pointsToShow);
        
        setPanOffset(prev => Math.max(0, Math.min(maxPanOffset, prev + panChange)));
      }
      
      setLastDragX(touch.clientX);
      setLastDragY(touch.clientY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setIsPinching(false);
  };

  // Wheel zoom handler
  const handleWheel = (e) => {
    // Only prevent default for zoom interactions, not regular scrolling
    try {
      e.preventDefault();
    } catch (error) {
      // Passive event listener - preventDefault not allowed, continue without it
    }
    
    if (e.shiftKey) {
      // Shift + wheel = Y-axis zoom
      const yZoomSensitivity = 0.1;
      const yZoomChange = e.deltaY * yZoomSensitivity;
      setYZoomLevel(prev => Math.max(10, Math.min(200, prev - yZoomChange)));
    } else {
      // Default wheel = X-axis zoom
      const xZoomSensitivity = 0.1;
      const xZoomChange = e.deltaY * xZoomSensitivity;
      setZoomLevel(prev => Math.max(10, Math.min(100, prev - xZoomChange)));
    }
  };

  // Calculate volatility-based zone width
  const calculateVolatility = (data) => {
    if (!data || data.length < 20) return 0.02; // Default 2% for insufficient data
    
    // Calculate daily returns for the last 30 trading days
    const recentData = data.slice(-30);
    const returns = [];
    
    for (let i = 1; i < recentData.length; i++) {
      const dailyReturn = (recentData[i].close - recentData[i-1].close) / recentData[i-1].close;
      returns.push(Math.abs(dailyReturn));
    }
    
    if (returns.length === 0) return 0.02;
    
    // Calculate average daily volatility
    const avgVolatility = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
    
    // Classify volatility and return appropriate zone width percentage
    if (avgVolatility <= 0.015) {
      // Low volatility: ±1–2%
      return Math.max(0.01, Math.min(0.02, avgVolatility * 1.5));
    } else if (avgVolatility <= 0.03) {
      // Medium volatility: ±2–4%
      return Math.max(0.02, Math.min(0.04, avgVolatility * 1.3));
    } else {
      // High volatility (tech growth stocks): ±3–6%
      return Math.max(0.03, Math.min(0.06, avgVolatility * 1.2));
    }
  };

  // Support and Resistance zone calculation functions
  const calculateSupportResistanceLevels = (data) => {
    if (!data || data.length < 20) return [];
    
    const prices = data.map(d => ({ low: d.low, high: d.high, close: d.close, date: d.date }));
    const lookback = Math.min(20, Math.floor(data.length / 10)); // Dynamic lookback period
    const volatilityZoneWidth = calculateVolatility(data);
    
    // Find pivot points (local minima and maxima)
    const pivots = [];
    for (let i = lookback; i < prices.length - lookback; i++) {
      const current = prices[i];
      const isLocalMin = prices.slice(i - lookback, i + lookback + 1)
        .every(p => current.low <= p.low);
      const isLocalMax = prices.slice(i - lookback, i + lookback + 1)
        .every(p => current.high >= p.high);
      
      if (isLocalMin) {
        pivots.push({ price: current.low, type: 'support', strength: 1, date: current.date });
      }
      if (isLocalMax) {
        pivots.push({ price: current.high, type: 'resistance', strength: 1, date: current.date });
      }
    }
    
    // Group similar price levels and calculate strength
    const tolerance = (Math.max(...prices.map(p => p.high)) - Math.min(...prices.map(p => p.low))) * 0.02; // 2% tolerance for grouping
    const groupedLevels = [];
    
    pivots.forEach(pivot => {
      const existing = groupedLevels.find(level => 
        Math.abs(level.price - pivot.price) <= tolerance && level.type === pivot.type
      );
      
      if (existing) {
        existing.strength += 1;
        existing.touches.push(pivot.date);
        existing.price = (existing.price * (existing.strength - 1) + pivot.price) / existing.strength; // Average price
      } else {
        groupedLevels.push({
          price: pivot.price,
          type: pivot.type,
          strength: 1,
          touches: [pivot.date]
        });
      }
    });
    
    // Classify levels based on strength and recent price action
    const currentPrice = prices[prices.length - 1].close;
    
    groupedLevels.forEach(level => {
      let classification = '';
      let color = '';
      let strokeWidth = 1;
      let opacity = 0.6;
      
      // Calculate zone boundaries based on volatility
      const zoneWidth = level.price * volatilityZoneWidth;
      level.lowerBound = level.price - zoneWidth;
      level.upperBound = level.price + zoneWidth;
      
      // Determine classification based on strength and position
      if (level.strength >= 4) {
        if (level.type === 'support') {
          classification = 'Strong Support Zone';
          color = '#10b981'; // green
          strokeWidth = 3;
          opacity = 0.8;
        } else {
          classification = 'Strong Resistance Zone';
          color = '#ef4444'; // red
          strokeWidth = 3;
          opacity = 0.8;
        }
      } else if (level.strength >= 3) {
        if (level.type === 'support') {
          classification = 'Major Support Zone';
          color = '#059669'; // darker green
          strokeWidth = 2.5;
          opacity = 0.7;
        } else {
          classification = 'Major Resistance Zone';
          color = '#dc2626'; // darker red
          strokeWidth = 2.5;
          opacity = 0.7;
        }
      } else if (level.strength >= 2) {
        if (level.type === 'support') {
          classification = 'Key Support Zone';
          color = '#34d399'; // lighter green
          strokeWidth = 2;
          opacity = 0.6;
        } else {
          classification = 'Key Resistance Zone';
          color = '#f87171'; // lighter red
          strokeWidth = 2;
          opacity = 0.6;
        }
      } else {
        if (level.type === 'support') {
          classification = 'Historical Support Zone';
          color = '#6ee7b7'; // very light green
          strokeWidth = 1.5;
          opacity = 0.5;
        } else {
          classification = 'Historical Resistance Zone';
          color = '#fca5a5'; // very light red
          strokeWidth = 1.5;
          opacity = 0.5;
        }
      }
      
      // Check for potential breakout/breakdown using zone boundaries
      if (level.type === 'resistance' && currentPrice > level.upperBound) {
        classification = 'Breakout Zone';
        color = '#8b5cf6'; // purple
        strokeWidth = 3;
        opacity = 0.9;
      } else if (level.type === 'support' && currentPrice < level.lowerBound) {
        classification = 'Breakdown Zone';
        color = '#f59e0b'; // orange
        strokeWidth = 3;
        opacity = 0.9;
      }
      
      level.classification = classification;
      level.color = color;
      level.strokeWidth = strokeWidth;
      level.opacity = opacity;
      level.volatilityZoneWidth = volatilityZoneWidth; // Store for display
    });
    
    // Sort by strength and proximity to current price
    return groupedLevels
      .filter(level => level.strength >= 2 || Math.abs(currentPrice - level.price) / currentPrice < 0.1) // Show strong levels or nearby levels
      .sort((a, b) => {
        // Prioritize by strength, then by proximity to current price
        if (a.strength !== b.strength) return b.strength - a.strength;
        return Math.abs(currentPrice - a.price) - Math.abs(currentPrice - b.price);
      })
      .slice(0, 12); // Limit to 12 most important levels
  };
  
  const supportResistanceLevels = calculateSupportResistanceLevels(data);

  const filteredData = getFilteredData();

  if (loading) {
    return (
      <div className="card loading-skeleton">
        <div className="skeleton-item" style={{height: '2rem', width: '25%', marginBottom: '1rem'}}></div>
        <div className="skeleton-item" style={{height: '16rem'}}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-state">
        <p>Error loading chart: {error}</p>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <BarChart3 className="empty-state-icon" />
        <p>Select a stock to view its price chart</p>
      </div>
    );
  }


  const formatXAxisLabel = (tickItem) => {
    if (!filteredData || filteredData.length === 0) return '';
    
    // Get date range from filtered data
    const dates = filteredData.map(d => new Date(d.date));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date(Math.max(...dates));
    
    // Calculate the time span in days
    const timeDiffMs = maxDate.getTime() - minDate.getTime();
    const timeDiffDays = timeDiffMs / (1000 * 60 * 60 * 24);
    
    const currentDate = new Date(tickItem);
    
    // More than 1 year: show year
    if (timeDiffDays > 365) {
      return currentDate.toLocaleDateString('en-US', { 
        year: 'numeric',
        month: 'short'
      });
    }
    // More than 3 months: show month and day
    else if (timeDiffDays > 90) {
      return currentDate.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric'
      });
    }
    // Less than 3 months: show month and day (same as 3+ months for consistency)
    else {
      return currentDate.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">
            {new Date(label).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {`${entry.name}: $${entry.value.toFixed(2)}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CandlestickTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">
            {new Date(label).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p style={{ color: '#f59e0b' }}>Open: ${data.open.toFixed(2)}</p>
          <p style={{ color: '#10b981' }}>High: ${data.high.toFixed(2)}</p>
          <p style={{ color: '#ef4444' }}>Low: ${data.low.toFixed(2)}</p>
          <p style={{ color: '#3b82f6' }}>Close: ${data.close.toFixed(2)}</p>
          <p style={{ color: data.close >= data.open ? '#10b981' : '#ef4444' }}>
            Change: ${(data.close - data.open).toFixed(2)} ({(((data.close - data.open) / data.open) * 100).toFixed(2)}%)
          </p>
        </div>
      );
    }
    return null;
  };


  const renderChart = () => {
    switch (chartType) {
      case 'area':
        return (
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tickFormatter={formatXAxisLabel} />
            <YAxis domain={getYAxisDomain()} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            {showSupportResistance && supportResistanceLevels.map((level, index) => (
              <React.Fragment key={index}>
                {/* Zone area */}
                <ReferenceArea
                  y1={level.lowerBound}
                  y2={level.upperBound}
                  fill={level.color}
                  fillOpacity={level.opacity * 0.2}
                  strokeWidth={0}
                />
                {/* Center line */}
                <ReferenceLine 
                  y={level.price}
                  stroke={level.color}
                  strokeWidth={level.strokeWidth}
                  strokeOpacity={level.opacity}
                  strokeDasharray={level.type === 'support' ? '5 5' : '3 3'}
                  label={{ 
                    value: `${level.classification}: $${level.lowerBound.toFixed(2)}-$${level.upperBound.toFixed(2)}`, 
                    position: 'topRight',
                    style: { 
                      fontSize: '10px', 
                      fill: level.color,
                      fontWeight: 'bold'
                    }
                  }}
                />
              </React.Fragment>
            ))}
            <Area 
              type="monotone" 
              dataKey="close" 
              stroke="#3b82f6" 
              fillOpacity={1} 
              fill="url(#colorPrice)"
              name="Close Price"
            />
          </AreaChart>
        );

      case 'volume':
        return (
          <BarChart data={filteredData}>
            <XAxis dataKey="date" tickFormatter={formatXAxisLabel} />
            <YAxis />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="volume" fill="#8b5cf6" name="Volume" />
          </BarChart>
        );

      case 'candlestick':
        return (
          <ComposedChart data={filteredData}>
            <XAxis dataKey="date" tickFormatter={formatXAxisLabel} />
            <YAxis domain={getYAxisDomain()} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CandlestickTooltip />} />
            {showSupportResistance && supportResistanceLevels.map((level, index) => (
              <React.Fragment key={index}>
                {/* Zone area */}
                <ReferenceArea
                  y1={level.lowerBound}
                  y2={level.upperBound}
                  fill={level.color}
                  fillOpacity={level.opacity * 0.2}
                  strokeWidth={0}
                />
                {/* Center line */}
                <ReferenceLine 
                  y={level.price}
                  stroke={level.color}
                  strokeWidth={level.strokeWidth}
                  strokeOpacity={level.opacity}
                  strokeDasharray={level.type === 'support' ? '5 5' : '3 3'}
                  label={{ 
                    value: `${level.classification}: $${level.lowerBound.toFixed(2)}-$${level.upperBound.toFixed(2)}`, 
                    position: 'topRight',
                    style: { 
                      fontSize: '10px', 
                      fill: level.color,
                      fontWeight: 'bold'
                    }
                  }}
                />
              </React.Fragment>
            ))}
            {/* High-Low lines */}
            <Line type="monotone" dataKey="high" stroke="transparent" strokeWidth={0} dot={false} />
            <Line type="monotone" dataKey="low" stroke="transparent" strokeWidth={0} dot={false} />
            {/* Open-Close bars */}
            <Bar 
              dataKey="open" 
              fill="transparent" 
              stroke="transparent"
              shape={({ payload, x, y, width, height }) => {
                if (!payload) return null;
                const { open, high, low, close } = payload;
                const isPositive = close >= open;
                const candleWidth = Math.max(width * 0.7, 3);
                const centerX = x + width / 2;
                
                // Calculate Y positions based on the chart's scale
                if (filteredData.length === 0) return null;
                const minVal = Math.min(...filteredData.map(d => Math.min(d.low, d.high, d.open, d.close)));
                const maxVal = Math.max(...filteredData.map(d => Math.max(d.low, d.high, d.open, d.close)));
                const range = maxVal - minVal || 1;
                
                const getY = (value) => y + height - ((value - minVal) / range) * height;
                
                const highY = getY(high);
                const lowY = getY(low);
                const openY = getY(open);
                const closeY = getY(close);
                const bodyTop = Math.min(openY, closeY);
                const bodyBottom = Math.max(openY, closeY);
                const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
                
                const color = isPositive ? '#10b981' : '#ef4444';
                
                return (
                  <g>
                    {/* High-Low wick */}
                    <line
                      x1={centerX}
                      y1={highY}
                      x2={centerX}
                      y2={lowY}
                      stroke={color}
                      strokeWidth={1}
                    />
                    {/* Open-Close body */}
                    <rect
                      x={centerX - candleWidth / 2}
                      y={bodyTop}
                      width={candleWidth}
                      height={bodyHeight}
                      fill={isPositive ? 'white' : color}
                      stroke={color}
                      strokeWidth={1.5}
                    />
                  </g>
                );
              }}
            />
          </ComposedChart>
        );

      default:
        return (
          <LineChart data={filteredData}>
            <XAxis dataKey="date" tickFormatter={formatXAxisLabel} />
            <YAxis domain={getYAxisDomain()} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip content={<CustomTooltip />} />
            {showSupportResistance && supportResistanceLevels.map((level, index) => (
              <React.Fragment key={index}>
                {/* Zone area */}
                <ReferenceArea
                  y1={level.lowerBound}
                  y2={level.upperBound}
                  fill={level.color}
                  fillOpacity={level.opacity * 0.2}
                  strokeWidth={0}
                />
                {/* Center line */}
                <ReferenceLine 
                  y={level.price}
                  stroke={level.color}
                  strokeWidth={level.strokeWidth}
                  strokeOpacity={level.opacity}
                  strokeDasharray={level.type === 'support' ? '5 5' : '3 3'}
                  label={{ 
                    value: `${level.classification}: $${level.lowerBound.toFixed(2)}-$${level.upperBound.toFixed(2)}`, 
                    position: 'topRight',
                    style: { 
                      fontSize: '10px', 
                      fill: level.color,
                      fontWeight: 'bold'
                    }
                  }}
                />
              </React.Fragment>
            ))}
            <Line 
              type="monotone" 
              dataKey="close" 
              stroke="#3b82f6" 
              strokeWidth={2} 
              dot={false}
              name="Close Price"
            />
          </LineChart>
        );
    }
  };

  return (
    <div className="card">
      <div className="chart-header">
        <h3 className="chart-title">
          {symbol} Price Chart
        </h3>
        
        <div className="chart-controls">
          <div className="chart-control-group">
            <button
              onClick={() => setChartType('line')}
              className={`chart-control-btn ${chartType === 'line' ? 'active' : ''}`}
            >
              <TrendingUp size={16} />
              Line
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`chart-control-btn ${chartType === 'area' ? 'active' : ''}`}
            >
              <Activity size={16} />
              Area
            </button>
            <button
              onClick={() => setChartType('candlestick')}
              className={`chart-control-btn ${chartType === 'candlestick' ? 'active' : ''}`}
            >
              <BarChart3 size={16} />
              Candlestick
            </button>
            <button
              onClick={() => setChartType('volume')}
              className={`chart-control-btn ${chartType === 'volume' ? 'active' : ''}`}
            >
              <Calendar size={16} />
              Volume
            </button>
          </div>
          
          <div className="chart-control-group date-range-controls">
            <span className="date-range-label">Range:</span>
            <button
              onClick={() => handleDateRangeChange('1y')}
              className={`date-range-btn ${dateRange === '1y' ? 'active' : ''}`}
            >
              {t('oneYear')}
            </button>
            <button
              onClick={() => handleDateRangeChange('2y')}
              className={`date-range-btn ${dateRange === '2y' ? 'active' : ''}`}
            >
              {t('twoYears')}
            </button>
            <button
              onClick={() => handleDateRangeChange('all')}
              className={`date-range-btn ${dateRange === 'all' ? 'active' : ''}`}
            >
              {t('allTime')}
            </button>
          </div>
          
          <div className="chart-control-group">
            <button
              onClick={handleToggleZoomView}
              className={`chart-control-btn ${zoomLevel >= 100 ? '' : 'active'}`}
              title={zoomLevel >= 100 ? 'Zoom in to recent data' : 'Show all trading dates'}
            >
              {zoomLevel >= 100 ? (
                <>
                  <Plus size={16} />
                  {t('zoom')}
                </>
              ) : (
                <>
                  <Eye size={16} />
                  {t('showAll')}
                </>
              )}
            </button>
            
            <button
              onClick={() => setShowSupportResistance(!showSupportResistance)}
              className={`chart-control-btn ${showSupportResistance ? 'active' : ''}`}
              title={showSupportResistance ? 'Hide Support/Resistance levels' : 'Show Support/Resistance levels'}
            >
              {showSupportResistance ? (
                <>
                  <Shield size={16} />
                  S/R
                </>
              ) : (
                <>
                  <AlertTriangle size={16} />
                  S/R
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div 
        className="chart-container"
        style={{ 
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
      >
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
        
        {/* Zoom controls toggle button */}
        <div style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 11
        }}>
          <button
            onClick={toggleZoomControls}
            style={{
              width: '48px',
              height: '48px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: showZoomControls ? '#3b82f6' : 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              color: showZoomControls ? 'white' : '#374151',
              opacity: 0.3
            }}
            title={showZoomControls ? t('zoom') : t('zoom')}
          >
            {showZoomControls ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>

        {/* Floating zoom controls */}
        {showZoomControls && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '56px', // Moved left to make room for toggle button
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            zIndex: 10
          }}>
          {/* X-axis (horizontal) zoom controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              textAlign: 'center',
              fontWeight: '600',
              marginBottom: '2px'
            }}>
              {t('timeframe')}
            </div>
            <button
              onClick={handleZoomIn}
              disabled={zoomLevel <= 10}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: zoomLevel <= 10 ? 'not-allowed' : 'pointer',
                opacity: zoomLevel <= 10 ? 0.5 : 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151'
              }}
              title={t('zoom')}
            >
              <Plus size={16} />
            </button>
            
            <button
              onClick={handleZoomOut}
              disabled={zoomLevel >= 100}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: zoomLevel >= 100 ? 'not-allowed' : 'pointer',
                opacity: zoomLevel >= 100 ? 0.5 : 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151'
              }}
              title="Zoom out horizontally (show more time periods)"
            >
              <Minus size={16} />
            </button>
            
            <div style={{
              width: '44px',
              height: '24px',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              color: '#6b7280',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              {Math.round(zoomLevel)}%
            </div>
          </div>

          {/* Y-axis (vertical) zoom controls */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{
              fontSize: '10px',
              color: '#6b7280',
              textAlign: 'center',
              fontWeight: '600',
              marginBottom: '2px'
            }}>
              Price
            </div>
            <button
              onClick={handleYZoomIn}
              disabled={yZoomLevel <= 10}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: yZoomLevel <= 10 ? 'not-allowed' : 'pointer',
                opacity: yZoomLevel <= 10 ? 0.5 : 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151'
              }}
              title="Zoom in vertically (narrow price range)"
            >
              <Plus size={16} />
            </button>
            
            <button
              onClick={handleYZoomOut}
              disabled={yZoomLevel >= 200}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: yZoomLevel >= 200 ? 'not-allowed' : 'pointer',
                opacity: yZoomLevel >= 200 ? 0.5 : 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#374151'
              }}
              title="Zoom out vertically (wider price range)"
            >
              <Minus size={16} />
            </button>
            
            <div style={{
              width: '44px',
              height: '24px',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              backgroundColor: 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '600',
              color: '#6b7280',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}>
              {Math.round(yZoomLevel)}%
            </div>
          </div>

          {/* Reset button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{
              fontSize: '10px',
              color: 'transparent',
              textAlign: 'center',
              fontWeight: '600',
              marginBottom: '2px'
            }}>
              .
            </div>
            <button
              onClick={handleResetZoom}
              style={{
                width: '48px',
                height: '96px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#374151'
              }}
              title="Reset all zoom and pan"
            >
              ⟲
            </button>
          </div>
          </div>
        )}
        
      </div>

      <div className="chart-info">
        {/* Chart Data Section - Mobile: Full width above, Desktop: Left column */}
        <div style={{
          marginBottom: window.innerWidth <= 768 ? '1rem' : '1.5rem'
        }}>
          {window.innerWidth <= 768 ? (
            /* Mobile: Chart Data as separate section */
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              marginBottom: '1rem'
            }}>
              <h5 style={{ 
                margin: '0 0 0.75rem 0', 
                fontSize: '1rem',
                fontWeight: '600',
                color: '#334155',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <BarChart3 size={16} />
                Chart Data
              </h5>
              <div style={{ 
                fontSize: '0.875rem',
                lineHeight: '1.5',
                color: '#64748b'
              }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#475569' }}>Showing:</strong> {filteredData.length} of {data.length} trading days
                  {dateRange !== 'all' && (
                    <span style={{ marginLeft: '8px', color: '#64748b' }}>
                      ({dateRange === '1y' ? '1 Year' : '2 Years'} range)
                    </span>
                  )}
                </div>
                
                <div style={{ marginBottom: '0.5rem' }}>
                  <strong style={{ color: '#475569' }}>Price Range:</strong> ${filteredData.length > 0 ? Math.min(...filteredData.map(d => d.low)).toFixed(2) : 'N/A'} - ${filteredData.length > 0 ? Math.max(...filteredData.map(d => d.high)).toFixed(2) : 'N/A'}
                </div>
                
                {data.length > 0 && filteredData.length > 0 && (
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#475569' }}>Date Range:</strong> {new Date(filteredData[0]?.date).toLocaleDateString()} to {new Date(filteredData[filteredData.length - 1]?.date).toLocaleDateString()}
                  </div>
                )}
                
                {(zoomLevel < 100 || yZoomLevel !== 100 || panOffset > 0) && (
                  <div style={{ 
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    backgroundColor: '#e2e8f0',
                    borderRadius: '4px',
                    fontSize: '0.75rem'
                  }}>
                    <strong style={{ color: '#475569' }}>View Settings:</strong>{' '}
                    {zoomLevel < 100 && `X-Zoom: ${Math.round(zoomLevel)}%`}
                    {yZoomLevel !== 100 && ` • Y-Zoom: ${Math.round(yZoomLevel)}%`}
                    {panOffset > 0 && ` • Offset: ${Math.round(panOffset)} days`}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Desktop: Combined Statistics & Information Section */
            <div style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              gap: '2rem',
              padding: '1.25rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              margin: '1rem',
            }}>
              {/* Historical Chart Data */}
              <div>
                <h5 style={{ 
                  margin: '0 0 0.75rem 0', 
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  color: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <BarChart3 size={14} />
                  Chart Data
                </h5>
                <div style={{ 
                  fontSize: '0.8125rem',
                  lineHeight: '1.5',
                  color: '#64748b'
                }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#475569' }}>Showing:</strong> {filteredData.length} of {data.length} trading days
                    {dateRange !== 'all' && (
                      <span style={{ marginLeft: '8px', color: '#64748b' }}>
                        ({dateRange === '1y' ? '1 Year' : '2 Years'} range)
                      </span>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#475569' }}>Price Range:</strong> ${filteredData.length > 0 ? Math.min(...filteredData.map(d => d.low)).toFixed(2) : 'N/A'} - ${filteredData.length > 0 ? Math.max(...filteredData.map(d => d.high)).toFixed(2) : 'N/A'}
                  </div>
                  
                  {data.length > 0 && filteredData.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ color: '#475569' }}>Date Range:</strong> {new Date(filteredData[0]?.date).toLocaleDateString()} to {new Date(filteredData[filteredData.length - 1]?.date).toLocaleDateString()}
                    </div>
                  )}
                  
                  {(zoomLevel < 100 || yZoomLevel !== 100 || panOffset > 0) && (
                    <div style={{ 
                      marginTop: '0.75rem',
                      padding: '0.5rem',
                      backgroundColor: '#e2e8f0',
                      borderRadius: '4px',
                      fontSize: '0.75rem'
                    }}>
                      <strong style={{ color: '#475569' }}>View Settings:</strong>{' '}
                      {zoomLevel < 100 && `X-Zoom: ${Math.round(zoomLevel)}%`}
                      {yZoomLevel !== 100 && ` • Y-Zoom: ${Math.round(yZoomLevel)}%`}
                      {panOffset > 0 && ` • Offset: ${Math.round(panOffset)} days`}
                    </div>
                  )}
                </div>
              </div>

              {/* Navigation Controls Guide */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <h5 style={{ 
                  margin: '0 0 0.75rem 0', 
                  fontSize: '0.9375rem',
                  fontWeight: '600',
                  color: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Move size={14} />
                  Navigation
                </h5>
                <div style={{
                  padding: '0.875rem',
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  lineHeight: '1.4'
                }}>
                  <div style={{ marginBottom: '0.25rem' }}>• <strong>Drag</strong> to pan through time</div>
                  <div style={{ marginBottom: '0.25rem' }}>• <strong>Shift+drag</strong> to zoom</div>
                  <div style={{ marginBottom: '0.25rem' }}>• <strong>Scroll</strong> to zoom X-axis</div>
                  <div>• <strong>Shift+scroll</strong> to zoom Y-axis</div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Controls for Mobile - shown below chart data */}
          {window.innerWidth <= 768 && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px'
            }}>
              <h5 style={{ 
                margin: '0 0 0.75rem 0', 
                fontSize: '1rem',
                fontWeight: '600',
                color: '#334155',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <Move size={16} />
                Navigation
              </h5>
              <div style={{
                padding: '0.75rem',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.8125rem',
                color: '#6b7280',
                lineHeight: '1.4'
              }}>
                {window.innerWidth <= 480 ? (
                  <>
                    <div style={{ marginBottom: '0.25rem' }}>• <strong>Drag</strong> to navigate</div>
                    <div style={{ marginBottom: '0.25rem' }}>• <strong>Shift+drag</strong> to zoom</div>
                    <div>• <strong>Scroll</strong> for zoom controls</div>
                  </>
                ) : (
                  <>
                    <div style={{ marginBottom: '0.25rem' }}>• <strong>Drag</strong> to pan through data</div>
                    <div style={{ marginBottom: '0.25rem' }}>• <strong>Shift+drag</strong> to zoom</div>
                    <div>• <strong>Scroll</strong> for zoom controls</div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Support/Resistance Analysis Section */}
        {showSupportResistance && supportResistanceLevels.length > 0 && (
          <div style={{
            padding: window.innerWidth <= 480 ? '1.25rem' : window.innerWidth <= 768 ? '1rem' : '1.25rem',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <h5 style={{ 
              margin: '0 0 1rem 0', 
              fontSize: window.innerWidth <= 480 ? '1.125rem' : window.innerWidth <= 768 ? '1rem' : '0.9375rem', 
              fontWeight: '600', 
              color: '#334155',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <Shield size={window.innerWidth <= 480 ? 18 : window.innerWidth <= 768 ? 16 : 14} />
              <span>Support & Resistance Analysis</span>
              <span style={{ 
                fontSize: window.innerWidth <= 480 ? '0.875rem' : '0.8rem',
                color: '#64748b',
                fontWeight: '500'
              }}>
                ({supportResistanceLevels.length} zones identified)
              </span>
            </h5>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 480 
                ? '1fr' 
                : window.innerWidth <= 768 
                  ? 'repeat(auto-fit, minmax(280px, 1fr))'
                  : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: window.innerWidth <= 480 ? '1rem' : window.innerWidth <= 768 ? '0.75rem' : '0.5rem'
            }}>
              {supportResistanceLevels.map((level, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  gap: window.innerWidth <= 480 ? '8px' : '6px',
                  padding: window.innerWidth <= 480 ? '12px 14px' : window.innerWidth <= 768 ? '10px 12px' : '8px 10px',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  border: `1px solid ${level.color}30`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                  minHeight: 'fit-content'
                }}>
                  {/* Price and indicator row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%'
                  }}>
                    <div style={{
                      width: window.innerWidth <= 480 ? '24px' : window.innerWidth <= 768 ? '20px' : '16px',
                      height: window.innerWidth <= 480 ? '4px' : window.innerWidth <= 768 ? '3px' : '2px',
                      backgroundColor: level.color,
                      opacity: level.opacity,
                      borderRadius: '2px',
                      flexShrink: 0
                    }}></div>
                    <span style={{ 
                      color: level.color, 
                      fontWeight: '600',
                      fontSize: window.innerWidth <= 480 ? '1rem' : window.innerWidth <= 768 ? '0.9375rem' : '0.875rem',
                      lineHeight: '1.2'
                    }}>
                      ${level.lowerBound.toFixed(2)}-${level.upperBound.toFixed(2)}
                    </span>
                  </div>
                  
                  {/* Classification and details row */}
                  <div style={{
                    width: '100%',
                    paddingLeft: window.innerWidth <= 480 ? '34px' : window.innerWidth <= 768 ? '30px' : '26px'
                  }}>
                    <div style={{ 
                      color: '#6b7280', 
                      fontSize: window.innerWidth <= 480 ? '0.9375rem' : window.innerWidth <= 768 ? '0.875rem' : '0.8125rem',
                      lineHeight: '1.4',
                      fontWeight: '500'
                    }}>
                      {level.classification}
                    </div>
                    <div style={{ 
                      color: '#9ca3af', 
                      fontSize: window.innerWidth <= 480 ? '0.8125rem' : window.innerWidth <= 768 ? '0.75rem' : '0.6875rem',
                      lineHeight: '1.3',
                      marginTop: '2px'
                    }}>
                      {level.strength} {level.strength === 1 ? 'touch' : 'touches'}
                      {window.innerWidth > 480 && `, ${(level.volatilityZoneWidth * 100).toFixed(1)}% width`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Enhanced Legend Section */}
            <div style={{
              marginTop: window.innerWidth <= 480 ? '1.5rem' : window.innerWidth <= 768 ? '1.25rem' : '1rem',
              padding: window.innerWidth <= 480 ? '1rem' : window.innerWidth <= 768 ? '0.875rem' : '0.75rem',
              fontSize: window.innerWidth <= 480 ? '0.9375rem' : window.innerWidth <= 768 ? '0.875rem' : '0.8125rem',
              color: '#6b7280',
              lineHeight: '1.6',
              backgroundColor: 'white',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ marginBottom: window.innerWidth <= 480 ? '0.75rem' : '0.5rem' }}>
                <strong style={{ color: '#374151', fontSize: 'inherit' }}>About Zones:</strong> Price ranges based on volatility and trading activity
              </div>
              
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: window.innerWidth <= 480 ? '1fr' : 'repeat(2, 1fr)',
                gap: window.innerWidth <= 480 ? '0.5rem' : '0.75rem',
                marginBottom: window.innerWidth <= 480 ? '0.75rem' : '0.5rem'
              }}>
                <div><strong style={{ color: '#374151' }}>Strong</strong> (4+ touches)</div>
                <div><strong style={{ color: '#374151' }}>Major</strong> (3 touches)</div>
                <div><strong style={{ color: '#374151' }}>Key</strong> (2 touches)</div>
                <div><strong style={{ color: '#374151' }}>Historical</strong> (1 touch)</div>
              </div>
              
              <div style={{
                borderTop: window.innerWidth <= 480 ? '1px solid #f3f4f6' : 'none',
                paddingTop: window.innerWidth <= 480 ? '0.75rem' : '0'
              }}>
                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>Breakout</span> (resistance broken) • 
                <span style={{ color: '#f59e0b', fontWeight: '600' }}>Breakdown</span> (support broken)
              </div>
            </div>
          </div>
        )}
        </div>                
      </div>
    </div>
  );
};

export default StockChart;